import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Test Generation nodes - showing evolution of unit tests
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisTestGenPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    // User Intent
    {
      id: 'user-intent',
      data: { label: '💬 "Generate comprehensive unit tests for my authentication module"' },
      position: { x: 400, y: 50 },
      style: themeStyles.problemNode,
    },
    
    // Code Analysis
    {
      id: 'code-analysis',
      data: { label: '📊 Code Analysis\n• Auth module: 500 LOC\n• Current coverage: 45%\n• Complex edge cases\n• Security critical' },
      position: { x: 800, y: 50 },
      style: { ...themeStyles.analysisNode, width: 220 },
    },
    
    // Initial Test Generation
    {
      id: 'initial-tests',
      type: 'collapsibleCode',
      data: { 
        filename: 'auth.test.js',
        code: '// Basic test coverage\n' +
              'describe("login", () => {\n' +
              '  test("successful login", () => {\n' +
              '    expect(login("user", "pass")).toBe(true);\n' +
              '  });\n' +
              '  \n' +
              '  test("failed login", () => {\n' +
              '    expect(login("user", "wrong")).toBe(false);\n' +
              '  });\n' +
              '});',
        issues: 'Low coverage (45%), missing edge cases, no error handling tests',
        score: 2.0
      },
      position: { x: 400, y: 200 },
    },
    
    // Scoring System
    {
      id: 'scoring-label',
      data: { label: '🎯 Multi-Dimensional Scoring System' },
      position: { x: 1100, y: 200 },
      style: { ...themeStyles.metricNode, width: 240 },
    },
    {
      id: 'coverage-score',
      data: { label: '📊 Code Coverage\nLine: 45%\nBranch: 30%' },
      position: { x: 950, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'edge-score',
      data: { label: '🔍 Edge Cases\nScore: 2/5\nMissing: nulls, concurrent' },
      position: { x: 1100, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'security-score',
      data: { label: '🔒 Security Tests\nScore: 1/5\nNo injection tests' },
      position: { x: 1250, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'exec-feedback',
      data: { label: '⚡ Execution Feedback\n✅ 2/2 tests pass\n⏱️ Runtime: 45ms' },
      position: { x: 1025, y: 380 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'human-feedback',
      data: { label: '👤 Human Review\n"Missing timeout tests"\nScore: 2.5/5' },
      position: { x: 1175, y: 380 },
      style: themeStyles.suggestionNode,
    },
    
    // Genetic Algorithm Engine
    {
      id: 'genetic-algo',
      data: { label: '🧬 Genetic Algorithm Engine\n\n🔄 Mutation: Vary test patterns & edge cases\n🔗 Crossover: Combine best test strategies\n📈 Selection: Keep high-coverage variants\n🎲 Population: 20 test file variants' },
      position: { x: 100, y: 500 },
      style: { ...themeStyles.uniqueFeatureNode, width: 300, height: 140 },
    },
    
    // AI Agents with Prompts
    {
      id: 'ai-agents',
      data: { label: '🤖 Specialized Test Generation Agents' },
      position: { x: 500, y: 500 },
      style: { ...themeStyles.analysisNode, width: 260 },
    },
    {
      id: 'test-expert',
      data: { label: '🧪 Test Expert Agent\n\n📝 Prompt:\n"Generate comprehensive unit tests\ncovering all functions, edge cases,\nand error scenarios. Include:\n- Null/undefined inputs\n- Boundary conditions\n- Invalid data types"' },
      position: { x: 200, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'security-tester',
      data: { label: '🔒 Security Test Agent\n\n📝 Prompt:\n"Create security-focused tests for:\n- SQL injection attempts\n- XSS vulnerabilities\n- Timing attacks\n- Brute force protection\n- Session hijacking"' },
      position: { x: 500, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'coverage-optimizer',
      data: { label: '📈 Coverage Optimizer\n\n📝 Prompt:\n"Analyze uncovered code paths.\nGenerate tests to reach 95%+\ncoverage. Focus on:\n- Untested branches\n- Error handling paths\n- Edge case combinations"' },
      position: { x: 800, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    
    // Generation 1 Label
    {
      id: 'gen1-label',
      data: { label: '🌱 Generation 1: Enhanced Test Suites (Population: 20)' },
      position: { x: 500, y: 800 },
      style: { ...themeStyles.metricNode, width: 350 },
    },
    
    // Generation 1 Variants
    {
      id: 'gen1-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'auth.test.js (Variant A)',
        code: '// Comprehensive test suite\n' +
              '+ describe("Authentication", () => {\n' +
              '+   describe("login", () => {\n' +
              '+     test.each([\n' +
              '+       ["valid credentials", "user", "pass", true],\n' +
              '+       ["empty username", "", "pass", false],\n' +
              '+       ["null password", "user", null, false],\n' +
              '+       ["SQL injection", "admin\'--", "x", false]\n' +
              '+     ])("%s", (_, user, pass, expected) => {\n' +
              '+       expect(login(user, pass)).toBe(expected);\n' +
              '+     });\n' +
              '+     \n' +
              '+     test("handles undefined inputs", () => {\n' +
              '+       expect(login(undefined, undefined)).toBe(false);\n' +
              '+     });\n' +
              '+   });\n' +
              '+ });',
        score: 3.5
      },
      position: { x: 50, y: 900 },
    },
    {
      id: 'gen1-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'auth-security.test.js (Variant B)',
        code: '// Security-focused tests\n' +
              '+ describe("Security", () => {\n' +
              '+   test("prevents timing attacks", async () => {\n' +
              '+     const times = [];\n' +
              '+     for (let i = 0; i < 100; i++) {\n' +
              '+       const start = performance.now();\n' +
              '+       await login("user", "wrongpass");\n' +
              '+       times.push(performance.now() - start);\n' +
              '+     }\n' +
              '+     const variance = Math.max(...times) - Math.min(...times);\n' +
              '+     expect(variance).toBeLessThan(5);\n' +
              '+   });\n' +
              '+   \n' +
              '+   test("rate limiting", async () => {\n' +
              '+     for (let i = 0; i < 5; i++) {\n' +
              '+       await login("user", "wrong");\n' +
              '+     }\n' +
              '+     await expect(login("user", "wrong"))\n' +
              '+       .rejects.toThrow("Too many attempts");\n' +
              '+   });\n' +
              '+ });',
        score: 4.0
      },
      position: { x: 400, y: 900 },
    },
    {
      id: 'gen1-3',
      type: 'collapsibleCode',
      data: { 
        filename: 'auth-coverage.test.js (Variant C)',
        code: '// Coverage optimization\n' +
              '+ describe("Edge cases", () => {\n' +
              '+   test("handles concurrent logins", async () => {\n' +
              '+     const promises = Array(10).fill().map(() => \n' +
              '+       login("user", "pass")\n' +
              '+     );\n' +
              '+     const results = await Promise.all(promises);\n' +
              '+     expect(results.filter(r => r === true).length).toBe(1);\n' +
              '+   });\n' +
              '+   \n' +
              '+   test("session timeout", async () => {\n' +
              '+     const session = await login("user", "pass");\n' +
              '+     await new Promise(resolve => setTimeout(resolve, 3600000));\n' +
              '+     expect(isSessionValid(session)).toBe(false);\n' +
              '+   });\n' +
              '+ });',
        score: 3.8
      },
      position: { x: 750, y: 900 },
    },
    {
      id: 'gen1-more',
      data: { label: '... 17 more variants\nwith different test strategies' },
      position: { x: 950, y: 920 },
      style: { ...themeStyles.suggestionNode, width: 150 },
    },
    
    // Evolution Process
    {
      id: 'evolution-1',
      type: 'evolution',
      data: { 
        generation: '1→2',
        description: '🔄 Mutation & Crossover Applied',
        scores: {
          'Top Score': '4.0/5',
          'Coverage': '75%',
          'Security': '3.5/5',
          'Edge Cases': '4/5'
        }
      },
      position: { x: 400, y: 1100 },
    },
    
    // Generation 2 Label
    {
      id: 'gen2-label',
      data: { label: '🧬 Generation 2: Crossover Results (Best Features Combined)' },
      position: { x: 500, y: 1250 },
      style: { ...themeStyles.metricNode, width: 400 },
    },
    
    // Generation 2 - Combined Features
    {
      id: 'gen2-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'auth-comprehensive.test.js (Elite Variant)',
        code: '// Combined best features from Gen 1\n' +
              '+ describe("Authentication - Comprehensive", () => {\n' +
              '+   // From Variant A: Parameterized tests\n' +
              '+   test.each(testCases)("%s", testLogin);\n' +
              '+   \n' +
              '+   // From Variant B: Security tests\n' +
              '+   describe("Security", () => {\n' +
              '+     test("timing attack prevention", testTimingAttack);\n' +
              '+     test("rate limiting", testRateLimit);\n' +
              '+     test("SQL injection prevention", testSQLInjection);\n' +
              '+   });\n' +
              '+   \n' +
              '+   // From Variant C: Concurrency & timeouts\n' +
              '+   describe("Edge Cases", () => {\n' +
              '+     test("concurrent login handling", testConcurrency);\n' +
              '+     test("session timeout", testSessionTimeout);\n' +
              '+     test("memory leak prevention", testMemoryLeak);\n' +
              '+   });\n' +
              '+   \n' +
              '+   // New mutation: Performance tests\n' +
              '+   describe("Performance", () => {\n' +
              '+     test("login under 100ms", testPerformance);\n' +
              '+   });\n' +
              '+ });',
        score: 4.6
      },
      position: { x: 200, y: 1350 },
    },
    {
      id: 'gen2-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'auth-integration.test.js (Mutated Variant)',
        code: '// Mutation: Added integration tests\n' +
              '+ describe("Auth Integration", () => {\n' +
              '+   let mockDb, mockCache, mockLogger;\n' +
              '+   \n' +
              '+   beforeEach(() => {\n' +
              '+     mockDb = createMockDatabase();\n' +
              '+     mockCache = createMockCache();\n' +
              '+     mockLogger = createMockLogger();\n' +
              '+   });\n' +
              '+   \n' +
              '+   test("full authentication flow", async () => {\n' +
              '+     // Test DB connection, caching, logging\n' +
              '+     const result = await authenticateUser({\n' +
              '+       username: "testuser",\n' +
              '+       password: "testpass",\n' +
              '+       ip: "192.168.1.1"\n' +
              '+     });\n' +
              '+     \n' +
              '+     expect(mockDb.query).toHaveBeenCalled();\n' +
              '+     expect(mockCache.set).toHaveBeenCalled();\n' +
              '+     expect(mockLogger.info).toHaveBeenCalled();\n' +
              '+   });\n' +
              '+ });',
        score: 4.4
      },
      position: { x: 600, y: 1350 },
    },
    
    // Final Evolution
    {
      id: 'evolution-2',
      type: 'evolution',
      data: { 
        generation: '2→Final',
        description: '🏆 Elite Selection',
        scores: {
          'Coverage': '95%',
          'Security': '4.8/5',
          'Edge Cases': '4.9/5',
          'Performance': '4.7/5'
        }
      },
      position: { x: 400, y: 1550 },
    },
    
    // Final Output
    {
      id: 'final-output',
      type: 'collapsibleCode',
      data: { 
        filename: 'auth.test.js (Production Ready)',
        code: '// Final evolved test suite\n' +
              '// Coverage: 95% | Security: 4.8/5 | Performance: Optimized\n' +
              '\n' +
              'import { describe, test, expect, beforeEach, afterEach } from "@jest/globals";\n' +
              'import { login, logout, validateSession } from "./auth";\n' +
              'import { setupTestDB, teardownTestDB } from "./test-utils";\n' +
              '\n' +
              '// Comprehensive test suite with:\n' +
              '// ✅ 95% code coverage\n' +
              '// ✅ Security vulnerability tests\n' +
              '// ✅ Performance benchmarks\n' +
              '// ✅ Edge case handling\n' +
              '// ✅ Integration tests\n' +
              '// ✅ Concurrent operation tests\n' +
              '\n' +
              '// Total: 47 test cases\n' +
              '// Execution time: 1.2s\n' +
              '// All tests passing ✅',
        score: 4.8
      },
      position: { x: 400, y: 1700 },
    },
    
    // Benefits Box
    {
      id: 'benefits',
      data: { label: '✨ Evolution Benefits\n\n• 45% → 95% coverage\n• Found 3 security vulnerabilities\n• Discovered 5 edge case bugs\n• Reduced test runtime by 40%\n• Zero human intervention needed' },
      position: { x: 800, y: 1650 },
      style: { ...themeStyles.uniqueFeatureNode, width: 250, height: 140 },
    }
  ];
};

const testGenEdges = [
  // Initial flow
  { id: 'e1', source: 'user-intent', target: 'code-analysis', animated: true, style: themeStyles.edgeStyle },
  { id: 'e2', source: 'code-analysis', target: 'initial-tests', style: themeStyles.edgeStyle },
  { id: 'e3', source: 'initial-tests', target: 'scoring-label', animated: true, style: themeStyles.edgeStyle },
  
  // Scoring connections
  { id: 'e4', source: 'scoring-label', target: 'coverage-score', style: themeStyles.edgeStyle },
  { id: 'e5', source: 'scoring-label', target: 'edge-score', style: themeStyles.edgeStyle },
  { id: 'e6', source: 'scoring-label', target: 'security-score', style: themeStyles.edgeStyle },
  { id: 'e7', source: 'scoring-label', target: 'exec-feedback', style: themeStyles.edgeStyle },
  { id: 'e8', source: 'scoring-label', target: 'human-feedback', style: themeStyles.edgeStyle },
  
  // Genetic Algorithm
  { id: 'e9', source: 'initial-tests', target: 'genetic-algo', style: themeStyles.evolutionEdgeStyle },
  { id: 'e10', source: 'genetic-algo', target: 'ai-agents', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // AI Agents
  { id: 'e11', source: 'ai-agents', target: 'test-expert', style: themeStyles.edgeStyle },
  { id: 'e12', source: 'ai-agents', target: 'security-tester', style: themeStyles.edgeStyle },
  { id: 'e13', source: 'ai-agents', target: 'coverage-optimizer', style: themeStyles.edgeStyle },
  
  // Generation 1
  { id: 'e14', source: 'test-expert', target: 'gen1-1', style: themeStyles.edgeStyle },
  { id: 'e15', source: 'security-tester', target: 'gen1-2', style: themeStyles.edgeStyle },
  { id: 'e16', source: 'coverage-optimizer', target: 'gen1-3', style: themeStyles.edgeStyle },
  { id: 'e17', source: 'ai-agents', target: 'gen1-more', style: { ...themeStyles.edgeStyle, strokeDasharray: '5 5' } },
  
  // Evolution to Gen 1
  { id: 'e18', source: 'gen1-1', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  { id: 'e19', source: 'gen1-2', target: 'evolution-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e20', source: 'gen1-3', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  
  // Feedback loops
  { id: 'f1', source: 'coverage-score', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f2', source: 'exec-feedback', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f3', source: 'human-feedback', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  
  // Generation 2
  { id: 'e21', source: 'evolution-1', target: 'gen2-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e22', source: 'evolution-1', target: 'gen2-2', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Final Evolution
  { id: 'e23', source: 'gen2-1', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  { id: 'e24', source: 'gen2-2', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  
  // Final Output
  { id: 'e25', source: 'evolution-2', target: 'final-output', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e26', source: 'final-output', target: 'benefits', style: themeStyles.edgeStyle },
];

const ArtemisTestGeneration = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(testGenEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style,
        type: node.type,
        data: node.data
      }));
      localStorage.setItem('artemisTestGenPositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisTestGenPositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #F0F4F8 0%, #D9E2EC 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#102A43', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          🧪 Artemis Test Generation - Evolving Unit Tests
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(16, 42, 67, 0.08)',
          borderRadius: '20px',
          color: '#486581',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100
        }}>
          Watch how genetic algorithms evolve basic tests into comprehensive test suites
        </div>

        {/* Reset button */}
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: '#627D98',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            cursor: 'pointer',
            zIndex: 100,
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = '#486581'}
          onMouseLeave={(e) => e.target.style.background = '#627D98'}
        >
          Reset Layout
        </button>
        
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 120px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.1,
              minZoom: 0.3,
              maxZoom: 1.0
            }}
            minZoom={0.2}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 120,
                width: 180,
                backgroundColor: 'rgba(255,255,255,0.8)',
                border: '1px solid #D9E2EC'
              }}
              nodeColor={(node) => {
                return node.style?.background || '#627D98';
              }}
              nodeStrokeColor={() => '#486581'}
              nodeBorderRadius={2}
            />
            <Controls />
            <Background 
              variant="dots" 
              color="#D9E2EC" 
              gap={20} 
              size={1} 
            />
          </ReactFlow>
        </div>

        {/* Key Insights */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          background: 'rgba(255,255,255,0.95)',
          padding: '15px',
          borderRadius: '15px',
          border: '1px solid #D9E2EC',
          color: '#102A43',
          fontSize: '13px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          maxWidth: '320px'
        }}>
          <div style={{ marginBottom: '10px', fontWeight: '600', color: '#5E60CE' }}>
            🧬 Genetic Algorithm Approach
          </div>
          <div style={{ fontSize: '12px', lineHeight: '1.5', color: '#486581' }}>
            Artemis generates <span style={{ color: '#F4511E', fontWeight: '600' }}>populations of test variants</span>, 
            evaluates them across multiple dimensions, then uses 
            <span style={{ color: '#5E60CE', fontWeight: '600' }}> mutation and crossover</span> to 
            combine the best features into superior test suites.
          </div>
          <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(94, 96, 206, 0.1)', borderRadius: '8px' }}>
            <strong>Coverage Improvement:</strong> 45% → 95% 
            <span style={{ color: '#28A745', fontWeight: '600' }}> (+111%)</span>
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisTestGeneration; 